// src/utils/shiftTime.js
// SHARED CLOCKED-TIME CALCULATION: shifttime-c9d2b7e4
//
// The ONLY place a shift's clocked duration may be computed. Pure logic — no UI,
// no React, no Firestore imports.
//
// KEEP IN SYNC with the other repo's src/utils/shiftTime.js. This file is meant
// to be BYTE-IDENTICAL across saas-suite-platform (web) and spatchy-mobile — a
// reviewer should be able to `diff` the two and see zero divergence. If you
// change anything here, mirror it there in the same commit. (Same convention as
// shiftGuard.js / shiftguard-4d2a8f61.)
//
// EXPORTS (diff-at-a-glance list):
//   shiftDurationMs(shift)       -> signed ms | null   (core)
//   shiftDurationHours(shift)    -> exact float hours | null   (web payroll)
//   shiftDurationMinutes(shift)  -> signed whole minutes | null (mobile display)
//   isShiftPaid(shift)           -> boolean   (3c READ side — frozen flag, absent = paid)
//   isActivityPaid(type, paidMap)-> boolean   (3c WRITE side — resolve flag from settings)

// Accepts a Firestore Timestamp, a JS Date, an ISO string, or null/undefined.
const toDateSafe = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  return new Date(ts);
};

// CORE: signed milliseconds between clock-in and clock-out.
// null = incomplete shift (either timestamp missing) — contributes nothing anywhere.
// Deliberately NOT clamped: web historically sums a negative-length shift as
// negative; mobile ignores it. Each caller keeps its own historical handling.
export const shiftDurationMs = (shift) => {
  const start = toDateSafe(shift?.shiftStart);
  const end = toDateSafe(shift?.shiftEnd);
  if (!start || !end) return null;
  return end.getTime() - start.getTime();
};

// WEB PAYROLL projection: exact fractional hours, unrounded.
// Rounding is a display concern (.toFixed(2) at render) — never done here.
export const shiftDurationHours = (shift) => {
  const ms = shiftDurationMs(shift);
  return ms === null ? null : ms / 3600000;
};

// MOBILE DISPLAY projection: nearest whole minute per shift, signed.
// Mobile call sites keep their historical negative handling ('—' in the row
// label; clamp-to-0 in the weekly sum) — see TimesheetsScreen.js.
export const shiftDurationMinutes = (shift) => {
  const ms = shiftDurationMs(shift);
  return ms === null ? null : Math.round(ms / 60000);
};

// ── PAID / UNPAID (Phase 3c) ─────────────────────────────────────────────────
// Paid-ness is FROZEN per record at write time — a `paid` boolean stamped when
// the shift is created — NOT classified from activityType at read time. So
// flipping a business's paid/unpaid setting applies going-forward only: an
// approved week's pay never silently changes. Absent `paid` (every pre-3c
// record, and any record written by an old build mid-rollout) reads back as
// PAID, so no historical figure moves. Only an explicit `false` is unpaid.

// READ side — the ONE place a stored record's paid-ness is decided. Timesheet /
// payroll sums count a shift's hours as paid iff this returns true.
export const isShiftPaid = (shift) => shift?.paid !== false;

// WRITE side — resolve the flag to stamp from the org's paid-activity settings
// at create time. paidMap is { work, travel, break, shop } booleans; a missing
// map or key defaults to PAID (nothing is unpaid until a business says so).
export const isActivityPaid = (activityType, paidMap) =>
  paidMap?.[activityType || 'work'] !== false;
